import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { Edit, CheckSquare, Trash2, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import './TasksPage.css';

/**
 * Tasks page - Task management with RBAC.
 * Staff can create/assign tasks, clients can only view/complete their own.
 */
export const TasksPage = () => {
  const { user, hasFeature } = useEntitlements();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Filtering and sorting state
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to_type: 'user',
    assigned_to: '',
    assigned_to_client: '',
    priority: 'medium',
    due_date: '',
    source: 'manual',
  });
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    assigned_to_type: 'user',
    assigned_to: '',
    assigned_to_client: '',
    priority: 'medium',
    due_date: '',
    status: 'open',
  });
  
  const [staffUsers, setStaffUsers] = useState([]);
  const [clients, setClients] = useState([]);

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  useEffect(() => {
    fetchTasks();
    if (isStaff) {
      fetchStaffUsers();
      fetchClients();
    }
  }, [isStaff]);
  
  useEffect(() => {
    // Apply filters and sorting
    let filtered = [...tasks];
    
    // Apply status filter
    if (filterStatus) {
      filtered = filtered.filter(task => task.status === filterStatus);
    }
    
    // Apply priority filter
    if (filterPriority) {
      filtered = filtered.filter(task => task.priority === filterPriority);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'title':
          aVal = a.title?.toLowerCase() || '';
          bVal = b.title?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case 'due_date':
          aVal = a.due_date ? new Date(a.due_date) : new Date(0);
          bVal = b.due_date ? new Date(b.due_date) : new Date(0);
          break;
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'assigned_to':
          aVal = a.assigned_to_name || a.assigned_to_client_name || '';
          bVal = b.assigned_to_name || b.assigned_to_client_name || '';
          break;
        default:
          aVal = a[sortField] || '';
          bVal = b[sortField] || '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredTasks(filtered);
  }, [tasks, filterStatus, filterPriority, sortField, sortDirection]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/tasks/');
      setTasks(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStaffUsers = async () => {
    try {
      const response = await api.get('/v1/users/?role=staff');
      setStaffUsers(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch staff users:', err);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await api.get('/v1/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();

    // Validate assignment before submitting
    if (formData.assigned_to_type === 'user' && !formData.assigned_to) {
      setError('Please select a user to assign this task to.');
      return;
    }
    if (formData.assigned_to_type === 'client' && !formData.assigned_to_client) {
      setError('Please select a client to assign this task to.');
      return;
    }

    try {
      // Clean up the data before sending
      const taskData = {
        title: formData.title,
        description: formData.description,
        assigned_to_type: formData.assigned_to_type,
        priority: formData.priority,
        source: formData.source,
      };

      // Include assigned_to when assigning to user
      if (formData.assigned_to_type === 'user') {
        taskData.assigned_to = formData.assigned_to;
      }

      // Include assigned_to_client when assigning to client
      if (formData.assigned_to_type === 'client') {
        taskData.assigned_to_client = formData.assigned_to_client;
      }

      // Only include due_date if it's set
      if (formData.due_date) {
        taskData.due_date = formData.due_date;
      }

      await api.post('/v1/tasks/', taskData);
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        assigned_to_type: 'user',
        assigned_to: '',
        assigned_to_client: '',
        priority: 'medium',
        due_date: '',
        source: 'manual',
      });
      setError(null);
      fetchTasks();
    } catch (err) {
      // Improved error handling
      let errorMsg = 'Failed to create task';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else {
          // Handle field-specific errors
          const errors = [];
          for (const [field, messages] of Object.entries(err.response.data)) {
            if (Array.isArray(messages)) {
              errors.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errors.push(`${field}: ${messages}`);
            }
          }
          if (errors.length > 0) {
            errorMsg = errors.join(' | ');
          }
        }
      }
      setError(errorMsg);
    }
  };

  const handleViewTask = (task) => {
    setViewingTask(task);
    setEditFormData({
      title: task.title || '',
      description: task.description || '',
      assigned_to_type: task.assigned_to_type || 'user',
      assigned_to: task.assigned_to || '',
      assigned_to_client: task.assigned_to_client || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
      status: task.status || 'open',
    });
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!viewingTask) return;

    try {
      // Clean up the data before sending
      const taskData = {
        title: editFormData.title,
        description: editFormData.description,
        assigned_to_type: editFormData.assigned_to_type,
        priority: editFormData.priority,
        status: editFormData.status,
      };

      // Only include assigned_to if it's set
      if (editFormData.assigned_to_type === 'user' && editFormData.assigned_to) {
        taskData.assigned_to = editFormData.assigned_to;
      } else if (editFormData.assigned_to_type === 'user') {
        taskData.assigned_to = null;
      }

      // Only include assigned_to_client if it's set
      if (editFormData.assigned_to_type === 'client' && editFormData.assigned_to_client) {
        taskData.assigned_to_client = editFormData.assigned_to_client;
      } else if (editFormData.assigned_to_type === 'client') {
        taskData.assigned_to_client = null;
      }

      // Include due_date (can be empty to clear it)
      if (editFormData.due_date) {
        taskData.due_date = editFormData.due_date;
      } else {
        taskData.due_date = null;
      }

      await api.patch(`/v1/tasks/${viewingTask.id}/`, taskData);
      setShowEditModal(false);
      setViewingTask(null);
      setError(null);
      fetchTasks();
    } catch (err) {
      // Improved error handling
      let errorMsg = 'Failed to update task';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else {
          // Handle field-specific errors
          const errors = [];
          for (const [field, messages] of Object.entries(err.response.data)) {
            if (Array.isArray(messages)) {
              errors.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errors.push(`${field}: ${messages}`);
            }
          }
          if (errors.length > 0) {
            errorMsg = errors.join(' | ');
          }
        }
      }
      setError(errorMsg);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/v1/tasks/${taskId}/complete/`);
      fetchTasks();
      if (viewingTask && viewingTask.id === taskId) {
        setViewingTask(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/v1/tasks/${taskId}/`);
      fetchTasks();
      if (viewingTask && viewingTask.id === taskId) {
        setViewingTask(null);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete task');
    }
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} />;
    }
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const getStatusVariant = (status) => {
    const map = {
      open: 'neutral',
      in_progress: 'warning',
      blocked: 'danger',
      completed: 'success',
      cancelled: 'neutral',
    };
    return map[status] || 'neutral';
  };

  const getPriorityVariant = (priority) => {
    const map = {
      low: 'success',
      medium: 'warning',
      high: 'danger',
      urgent: 'danger',
    };
    return map[priority] || 'neutral';
  };

  if (loading) {
    return <div className="tasks-loading">Loading tasks...</div>;
  }

  return (
    <div className="tasks-page">
      <PageHeader
        icon={CheckSquare}
        title="Tasks"
        subtitle="Track work, due dates, and priorities across your team."
        primaryAction={isStaff ? (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Task
          </button>
        ) : null}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="tasks-filters">
        <div className="filter-group">
          <label>Filter by Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="blocked">Blocked</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Filter by Priority</label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort By</label>
          <select
            value={`${sortField}_${sortDirection}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('_');
              setSortField(field);
              setSortDirection(direction);
            }}
          >
            <option value="created_at_desc">Newest First</option>
            <option value="created_at_asc">Oldest First</option>
            <option value="due_date_asc">Due Date (Earliest)</option>
            <option value="due_date_desc">Due Date (Latest)</option>
            <option value="priority_desc">Priority (High to Low)</option>
            <option value="priority_asc">Priority (Low to High)</option>
            <option value="title_asc">Title (A-Z)</option>
            <option value="title_desc">Title (Z-A)</option>
            <option value="status_asc">Status (A-Z)</option>
            <option value="status_desc">Status (Z-A)</option>
          </select>
        </div>
      </div>

      <div className="tasks-list">
        {filteredTasks.length === 0 ? (
          <div className="tasks-empty">
            <p>
              {tasks.length === 0
                ? 'Your task queue is clear. Create a task to start the next workflow.'
                : 'No tasks match these filters. Try adjusting status, priority, or sort.'}
            </p>
            {isStaff && tasks.length === 0 && (
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First Task
              </button>
            )}
            {tasks.length > 0 && (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  setFilterStatus('');
                  setFilterPriority('');
                  setSortField('created_at');
                  setSortDirection('desc');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div 
              key={task.id} 
              className="task-card"
              onClick={() => handleViewTask(task)}
              style={{ cursor: 'pointer' }}
            >
              <div className="task-header">
                <h3>{task.title}</h3>
                <div className="task-badges">
                  <StatusBadge
                    className="task-status"
                    label={task.status.replace('_', ' ')}
                    variant={getStatusVariant(task.status)}
                  />
                  <StatusBadge
                    className="task-priority"
                    label={task.priority}
                    variant={getPriorityVariant(task.priority)}
                  />
                </div>
              </div>
              {task.description && (
                <p className="task-description">{task.description}</p>
              )}
              <div className="task-meta">
                {task.assigned_to_name && (
                  <span className="task-assigned">
                    Assigned to: {task.assigned_to_name} (Staff)
                  </span>
                )}
                {task.assigned_to_client_name && (
                  <span className="task-assigned">
                    Assigned to: {task.assigned_to_client_name} (Client)
                  </span>
                )}
                {task.due_date && (
                  <span className={`task-due ${task.is_overdue ? 'overdue' : ''}`}>
                    Due: {new Date(task.due_date).toLocaleDateString()}
                  </span>
                )}
                {task.source && (
                  <span className="task-source">Source: {task.source}</span>
                )}
              </div>
              <div className="task-actions" onClick={(e) => e.stopPropagation()}>
                {task.status !== 'completed' && task.status !== 'cancelled' && (
                  <>
                    {isStaff ? (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleCompleteTask(task.id)}
                      >
                        Mark Complete
                      </button>
                    ) : (
                      task.assigned_to === user?.id && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleCompleteTask(task.id)}
                        >
                          Complete Task
                        </button>
                      )
                    )}
                  </>
                )}
                {isStaff && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteTask(task.id)}
                    title="Delete Task"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Task Detail Side Panel */}
      {viewingTask && (
        <div className="detail-panel-overlay" onClick={() => { setViewingTask(null); setShowEditModal(false); }}>
          <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h2>{viewingTask.title}</h2>
              <button
                className="detail-panel-close"
                onClick={() => { setViewingTask(null); setShowEditModal(false); }}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="detail-panel-body">
              <section className="detail-section">
                <div className="task-badges" style={{ marginBottom: '1rem' }}>
                  <StatusBadge
                    className="task-status"
                    label={viewingTask.status.replace('_', ' ')}
                    variant={getStatusVariant(viewingTask.status)}
                  />
                  <StatusBadge
                    className="task-priority"
                    label={viewingTask.priority}
                    variant={getPriorityVariant(viewingTask.priority)}
                  />
                </div>
                {viewingTask.description && (
                  <div className="detail-field detail-field-full">
                    <label>Description</label>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{viewingTask.description}</span>
                  </div>
                )}
                <div className="detail-grid">
                  {viewingTask.assigned_to_name && (
                    <div className="detail-field">
                      <label>Assigned to (Staff)</label>
                      <span>{viewingTask.assigned_to_name}</span>
                    </div>
                  )}
                  {viewingTask.assigned_to_client_name && (
                    <div className="detail-field">
                      <label>Assigned to (Client)</label>
                      <span>{viewingTask.assigned_to_client_name}</span>
                    </div>
                  )}
                  {viewingTask.due_date && (
                    <div className="detail-field">
                      <label>Due Date</label>
                      <span className={viewingTask.is_overdue ? 'overdue' : ''}>
                        {new Date(viewingTask.due_date).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {viewingTask.source && (
                    <div className="detail-field">
                      <label>Source</label>
                      <span>{viewingTask.source}</span>
                    </div>
                  )}
                  <div className="detail-field">
                    <label>Created</label>
                    <span>{new Date(viewingTask.created_at).toLocaleString()}</span>
                  </div>
                  {viewingTask.updated_at && (
                    <div className="detail-field">
                      <label>Last Updated</label>
                      <span>{new Date(viewingTask.updated_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <div className="detail-panel-actions">
                  {isStaff && (
                    <>
                      <button className="btn btn-primary" onClick={() => setShowEditModal(true)}>
                        <Edit size={16} />
                        Edit Task
                      </button>
                      <button className="btn btn-danger" onClick={() => handleDeleteTask(viewingTask.id)}>
                        <Trash2 size={16} />
                        Delete Task
                      </button>
                    </>
                  )}
                  {viewingTask.status !== 'completed' && viewingTask.status !== 'cancelled' && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleCompleteTask(viewingTask.id)}
                    >
                      <CheckSquare size={16} />
                      Mark Complete
                    </button>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Task</h2>
            <form onSubmit={handleCreateTask}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={formData.assigned_to_type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    assigned_to_type: e.target.value,
                    assigned_to: '',
                    assigned_to_client: '',
                  })}
                >
                  <option value="user">Team Member</option>
                  <option value="client">Client</option>
                </select>
              </div>
              {formData.assigned_to_type === 'user' ? (
                <div className="form-group">
                  <label>Team Member</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {staffUsers.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} ({staff.email})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Client</label>
                  <select
                    value={formData.assigned_to_client}
                    onChange={(e) => setFormData({ ...formData, assigned_to_client: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="datetime-local"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && viewingTask && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Task</h2>
            <form onSubmit={handleUpdateTask}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={editFormData.assigned_to_type}
                  onChange={(e) => setEditFormData({ 
                    ...editFormData, 
                    assigned_to_type: e.target.value,
                    assigned_to: '',
                    assigned_to_client: '',
                  })}
                >
                  <option value="user">Team Member</option>
                  <option value="client">Client</option>
                </select>
              </div>
              {editFormData.assigned_to_type === 'user' ? (
                <div className="form-group">
                  <label>Team Member</label>
                  <select
                    value={editFormData.assigned_to}
                    onChange={(e) => setEditFormData({ ...editFormData, assigned_to: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {staffUsers.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} ({staff.email})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Client</label>
                  <select
                    value={editFormData.assigned_to_client}
                    onChange={(e) => setEditFormData({ ...editFormData, assigned_to_client: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Priority</label>
                <select
                  value={editFormData.priority}
                  onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="datetime-local"
                  value={editFormData.due_date}
                  onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

